import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Award, Globe2, ChevronRight, Send, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function AIAnalyzer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [requestFor, setRequestFor] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    api.get(`/films/${id}`).then((r) => setFilm(r.data));
  }, [id]);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/analyze-festivals", { film_id: id });
      setAnalysis(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const submitRequest = async () => {
    try {
      await api.post("/festivals/request", {
        film_id: id,
        festival_name: requestFor.name,
        notes: notes || `Please submit "${film.title}" to ${requestFor.name}`,
      });
      toast.success("Submission request received. Our team will be in touch.");
      setRequestFor(null);
      setNotes("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const scoreColor = (s) => s >= 80 ? "text-emerald-400" : s >= 60 ? "text-gold" : "text-foreground/70";

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-[1400px] mx-auto pb-20" data-testid="ai-analyzer-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> AI Festival Analyzer
            </p>
            <h1 className="font-display text-3xl sm:text-5xl font-black">{film?.title || "…"}</h1>
            <p className="text-foreground/60 mt-2 text-sm">Powered by Claude · Curated for African short films</p>
          </div>
          <Button onClick={runAnalysis} disabled={loading} className="bg-terracotta hover:bg-ember text-white h-11 px-6" data-testid="run-analysis-btn">
            <Sparkles className="w-4 h-4" /> {loading ? "Analyzing…" : analysis ? "Re-analyze" : "Run analysis"}
          </Button>
        </div>

        {loading && (
          <div className="space-y-4" data-testid="analyzing-state">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 rounded-xl bg-surface border border-terracotta/30 shimmer-bg animate-shimmer" />
            ))}
            <p className="text-center text-foreground/60 text-sm pt-4">Reading themes, genre, runtime and matching against festival criteria…</p>
          </div>
        )}

        {!loading && !analysis && (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center bg-surface/50" data-testid="analyzer-idle">
            <Award className="w-12 h-12 mx-auto text-terracotta/60 mb-3" />
            <h2 className="font-display text-2xl font-bold mb-2">Discover qualifying festivals</h2>
            <p className="text-foreground/60 max-w-md mx-auto mb-5">
              Our AI compares your film against international short film festival criteria and highlights the best matches.
            </p>
            <Button onClick={runAnalysis} className="bg-terracotta hover:bg-ember text-white" data-testid="idle-run-analysis-btn">
              <Sparkles className="w-4 h-4" /> Run analysis
            </Button>
          </div>
        )}

        {analysis && !loading && (
          <div className="space-y-6">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-terracotta/15 via-gold/5 to-transparent border border-terracotta/30">
              <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-2 flex items-center gap-1.5">
                <Trophy className="w-3 h-3" /> Summary
              </p>
              <p className="font-display text-lg sm:text-xl leading-relaxed">{analysis.summary}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="festival-results">
              {(analysis.festivals || []).map((f, i) => (
                <div key={`${f.name}-${i}`} className="p-6 rounded-xl bg-surface border border-white/10 hover:border-terracotta/40 transition-colors" data-testid={`festival-card-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display text-xl font-bold leading-tight">{f.name}</h3>
                      <p className="text-xs text-foreground/60 flex items-center gap-1 mt-1">
                        <Globe2 className="w-3 h-3" /> {f.country} · {f.category}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`font-display font-black text-2xl ${scoreColor(f.match_score)}`}>{f.match_score}</p>
                      <p className="text-xs text-foreground/50">match</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed mb-4">{f.reasoning}</p>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-foreground/60 pt-3 border-t border-white/5">
                    <span>📅 {f.deadline_window || "Rolling"}</span>
                    <span>💵 ${f.submission_fee_usd ?? "—"} fee</span>
                  </div>
                  <Button
                    onClick={() => { setRequestFor(f); setNotes(""); }}
                    className="w-full mt-4 bg-white/5 hover:bg-terracotta hover:text-white text-foreground border border-white/10"
                    size="sm"
                    data-testid={`request-submit-btn-${i}`}
                  >
                    <Send className="w-3.5 h-3.5" /> Request submission help
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!requestFor} onOpenChange={(o) => !o && setRequestFor(null)}>
        <DialogContent className="bg-surface border-white/10 text-foreground">
          <DialogHeader>
            <DialogTitle>Request festival submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-foreground/70">
              Our team will help submit <strong>{film?.title}</strong> to <strong>{requestFor?.name}</strong>.
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific notes? (deadlines, dossier, etc.)"
              rows={4}
              className="bg-ink border-white/10"
              data-testid="festival-request-notes"
            />
          </div>
          <DialogFooter>
            <Button onClick={submitRequest} className="bg-terracotta hover:bg-ember text-white" data-testid="confirm-festival-request-btn">
              <Send className="w-4 h-4" /> Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
